'use client'

import { imageQuality, urlForImage } from '@repo/sanity/image'
import { ProductMedia } from '@repo/sanity/shorthand'
import { Lightbox, LightboxContent, LightboxDescription, LightboxHeader, LightboxTitle } from '@repo/ui/components/lightbox'
import { cn } from '@repo/ui/lib/utils'
import { Image, ImageProps } from 'next-sanity/image'
import { FunctionComponent, startTransition, useEffect, useState } from 'react'
import { useProduct, useUpdateURL } from '../../../providers/ProductProvider'
import { PageDto } from '../../../types/page.types'
import Video from '../../media/Video'
import ProductMediaLightboxButton from './ProductMediaLightboxButton'

interface Props {
  pageDto: PageDto
  media: ProductMedia[]
}

const ProductMediaLightbox: FunctionComponent<Props> = ({ pageDto, media }) => {
  const { state, updateLightboxMedia } = useProduct()
  const [open, setOpen] = useState(false)
  const updateURL = useUpdateURL()
  const mediaIndex = state.lightbox ? parseInt(state.lightbox) : undefined
  const currentMedia = mediaIndex !== undefined ? media?.[mediaIndex] : undefined
  const nextMediaIndex = mediaIndex !== undefined && mediaIndex + 1 < media.length ? mediaIndex + 1 : 0
  const previousMediaIndex = mediaIndex === undefined || mediaIndex === 0 ? media.length - 1 : mediaIndex - 1
  const isVideo = currentMedia ? 'videos' in currentMedia : false
  const videoMobile: any = currentMedia && isVideo && 'videos' in currentMedia ? currentMedia?.videos?.[0] : undefined
  const videoDesktop: any = currentMedia && isVideo && 'videos' in currentMedia ? currentMedia?.videos?.[1] : undefined
  const videoUrlMobile = videoMobile?.videoUrl || videoMobile?.videoFile?.asset?.url
  const videoUrlDesktop = videoDesktop?.videoUrl || videoDesktop?.videoFile?.asset?.url

  useEffect(() => {
    setOpen(!!currentMedia)
  }, [currentMedia])

  const handleOpenChange = (open: boolean) => {
    setOpen(open)

    if (!open) {
      startTransition(() => {
        const newState = updateLightboxMedia(undefined)

        updateURL(newState)
      })
    }
  }

  const baseImageProps: Partial<ImageProps> = {
    fill: true,
    priority: true,
    className: 'object-cover',
    sizes: '100vw',
    quality: Math.min(imageQuality + 10, 100),
  }

  return (
    <Lightbox open={open} onOpenChange={handleOpenChange} modal={true}>
      <LightboxContent>
        <LightboxHeader>
          <LightboxTitle>{pageDto.dictionary.lightbox?.media || 'Media'}</LightboxTitle>
          <LightboxDescription>{pageDto.dictionary.lightbox?.fullsize || 'Full size.'}</LightboxDescription>
        </LightboxHeader>
        <form className={cn('relative w-full', !isVideo && 'aspect-[2/3]')}>
          {currentMedia && isVideo && videoUrlMobile && (
            <Video
              src={videoUrlMobile}
              className={cn('absolute inset-0', videoUrlDesktop && 'lg:hidden')}
              poster={urlForImage(videoMobile?.videoPoster)?.width(600)?.url()}
              aria-label={videoMobile?.videoPoster?.alt}
            />
          )}
          {currentMedia && isVideo && videoUrlDesktop && (
            <Video
              src={videoUrlDesktop}
              className={cn('absolute inset-0 hidden lg:block')}
              poster={urlForImage(videoDesktop?.videoPoster)?.width(600)?.url()}
              aria-label={videoDesktop?.videoPoster?.alt}
            />
          )}
          {currentMedia && !isVideo && '_type' in currentMedia && (
            <Image
              {...baseImageProps}
              alt={currentMedia.alt || 'Miista'}
              src={currentMedia.asset?.url || ''}
              blurDataURL={currentMedia.asset?.metadata?.lqip ?? undefined}
              placeholder={currentMedia.asset?.metadata?.lqip ? 'blur' : undefined}
            />
          )}
          <ProductMediaLightboxButton
            direction='previous'
            aria-label={pageDto.dictionary.lightbox?.previous || 'Previous'}
            formAction={() => {
              const newState = updateLightboxMedia(previousMediaIndex.toString())

              updateURL(newState)
            }}
          />
          <ProductMediaLightboxButton
            direction='next'
            aria-label={pageDto.dictionary.lightbox?.next || 'Next'}
            formAction={() => {
              const newState = updateLightboxMedia(nextMediaIndex.toString())

              updateURL(newState)
            }}
          />
        </form>
      </LightboxContent>
    </Lightbox>
  )
}

export default ProductMediaLightbox
